<template>
  <tomb-modal :show="show" @input="closeModal">
    <div slot="heading">
      See North in action in your <br />
      own demo account
    </div>
    <p class="text-center mt-4">
      This demo account has sample data for you to see and experience what a fully functional North
      account looks like.
    </p>
    <div>
      <h3 class="text-normal-1 text-center mb-3 mt-4">Pick an example company</h3>
      <div
        class="source-org d-flex pointer"
        v-for="org in sourceOrgs"
        :key="org.id"
        :class="{ selected: org.id === selectedOrg }"
        @click="!createdOrg && (selectedOrg = org.id)"
      >
        <b-form-radio
          class="radio-item bold"
          v-model="selectedOrg"
          :disabled="!!createdOrg"
          name="some-radios"
          :value="org.id"
          >{{ org.name }}</b-form-radio
        >
        <div>{{ org.description }}</div>
      </div>
    </div>
    <div class="mt-5 mb-2 text-center">
      <div v-if="createdOrg" class="mb-3 text-center">
        Your demo account <strong>{{ createdOrg.name }}</strong> has been created.
      </div>
      <save-button
        btn-text="Go to your demo account"
        variant="success"
        class="px-4 d-inline-flex"
        @save="moveToDemoOrg"
        v-if="createdOrg"
      />
      <save-button
        class="px-4 d-inline-flex"
        btn-text="Create a North Demo Account"
        submit-btn-class="py-2 px-4"
        :is-loading="isSubmitting"
        @save="createDemoOrg"
        v-else
      />
    </div>
  </tomb-modal>
</template>

<script>
import { trackAnalyticsEvent } from '@/analytics'
import TombModal from '@/components/modal/TombModal.vue'
import SaveButton from '@/components/SaveButton.vue'
import { EVENT_ACTION, EVENT_CATEGORY } from '@/constants/analytics'
import { ORG_SWITCH_QUERY_NAME } from '@/constants/common'
import { goalsTabIds } from '@/constants/goals'
import { ACTIVE_QUARTER_ID, setDefaultIntervalId } from '@/interval'
import service from '@/service'
import urlManager from '@/urlManager'
import handleClickNavigation from '@/utils/handleClickNavigation'
import { getTeamPageRouteConfig } from '@/utils/navigationConfigs'
import showAPIError from '@/utils/showAPIError'
import { BFormRadio } from 'bootstrap-vue'
import { mapActions } from 'vuex'

/**
 * This modal provides the way to create a demo org
 */
export default {
  name: 'DemoOrgModal',
  props: ['show'],
  model: {
    prop: 'show'
  },
  components: { SaveButton, BFormRadio, TombModal },
  data() {
    return {
      isSubmitting: false,
      createdOrg: false,
      sourceOrgs: [],
      selectedOrg: null
    }
  },
  computed: {},
  methods: {
    ...mapActions(['getCurrentUser']),
    closeModal() {
      this.$emit('input')
    },
    async getSourceOrgs() {
      const response = await service.client.get(urlManager.demoOrgSources)
      this.sourceOrgs = response.data
      this.selectedOrg = this.sourceOrgs[0].id
    },
    async createDemoOrg() {
      try {
        this.isSubmitting = true
        const selectedOrg = this.sourceOrgs.find(org => org.id === this.selectedOrg)
        const response = await service.client.post(urlManager.orgs, {
          name: `${selectedOrg.name} (Demo)`,
          is_demo_org: true,
          source_org_id: selectedOrg.id
        })
        this.createdOrg = response.data
        await this.getCurrentUser()
        trackAnalyticsEvent(EVENT_CATEGORY.ACTIVITY, EVENT_ACTION.DEMO_ORG_CREATED)
      } catch (e) {
        showAPIError(e)
      } finally {
        this.isSubmitting = false
      }
    },
    moveToDemoOrg(event) {
      setDefaultIntervalId(ACTIVE_QUARTER_ID)
      const location = getTeamPageRouteConfig({
        orgId: this.createdOrg.id,
        teamId: this.createdOrg.rootTeamId,
        entity: goalsTabIds.goals,
        query: {
          [ORG_SWITCH_QUERY_NAME]: true
        }
      })
      this.closeModal()
      handleClickNavigation.call(this, event, location, true)
    }
  },
  async mounted() {
    await this.getSourceOrgs()
  }
}
</script>

<style lang="scss" scoped>
@import '~@/styles/_styleConfigurations.scss';

.source-org {
  background: $senary;
  border-radius: 20px;
  border: 1px solid $borderPrimary;
  padding: 6px 12px;
  width: 320px;
  margin: 12px auto 0 auto;
  &.selected {
    border-color: $brand;
  }
}

.radio-item {
  width: 130px;
}
</style>
